import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import axios, { AxiosError } from 'axios';
import { LogOut, setCurrentUser, setReferralCode } from './authReducer';
import { jwtDecode } from 'jwt-decode';
import keys from '../../config';
import { endpoints } from '../../utils/apiEndPoints';
import { authService } from '../../services/authServices';
import setAuthToken from '../../utils/setToken';
import { apiErrorHandler } from '../../utils/api_helper';

// Define types for the response data
interface LoginResponse {
  status: string;
  msg: string;
  data?: {
    token: string;
    referral_code: string;
    role: string;
  };
}

interface UserData {
  isChecked?: boolean;
  [key: string]: any;
}

interface ForgetPasswordResponse {
  status: string;
  message: string;
}

interface SetPasswordResponse {
  status: string;
  message: string;
}

interface VerifyOtpResponse {
  status: string;
  message: string;
}

interface ProfileResponse {
  data: {
    doctor_id: string;
    name: string;
    // Add more properties as needed
  };
}

export const LoginUser = createAsyncThunk<LoginResponse, UserData, { rejectValue: AxiosError }>(
  'auth/UserLogin',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post<LoginResponse>(keys.api_key + endpoints.Login, data, {
        /*  headers: {
           platform: 'web',
           lang: localStorage.getItem('user-language') || 'en',
         }, */
      });

      if (response.data.status === "success") {
        toast.success(response.data.msg);

        const token: string = response.data.data?.token || '';
        const referralCode: string = response.data.data?.referral_code || '';

        // Store the token and referral code
        localStorage.setItem('jwt_token', JSON.stringify(token));
        dispatch(setReferralCode(referralCode));
        try {
          const decoded = jwtDecode(token);
          setAuthToken(token);
          dispatch(setCurrentUser(decoded));
        } catch {
          toast.error('Invalid token received');
          throw new Error('Token decoding failed');
        }
      } else {
        toast.error(response.data.msg);
      }

      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          toast.error(error.response.data.msg);
        } else {
          toast.error('Network Error');
        }
      }
      return rejectWithValue(error);
    }
  }
);

export const forgetPassword = createAsyncThunk<ForgetPasswordResponse, any, { rejectValue: AxiosError }>(
  'auth/forgetPassword',
  async (data, { rejectWithValue }) => {
    try {
      const forgetpwd = await axios.post<ForgetPasswordResponse>(keys.api_key + endpoints.forgetPassword, data, {
        headers: {
          platform: 'web',
        },
      });
      return forgetpwd.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const setNewPassword = createAsyncThunk<SetPasswordResponse, any, { rejectValue: AxiosError }>(
  'auth/SetNewPassword',
  async (data, { rejectWithValue }) => {
    try {
      const setPassword = await axios.post<SetPasswordResponse>(keys.api_key + endpoints.SetPassword, data, {
        headers: {
          platform: 'web',
        },
      });
      return setPassword.data;
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const verifyOtp = createAsyncThunk<VerifyOtpResponse, any, { rejectValue: AxiosError }>(
  'auth/verifyOtp',
  async (data, { rejectWithValue }) => {
    try {
      const verifyOtp = await axios.post<VerifyOtpResponse>(keys.api_key + endpoints.verifyOtp, data);
      return verifyOtp.data;
    } catch (error: any) {
      if (error.response) {
        toast.error(error.response.data.msg);
      } else {
        toast.error('Network Error');
      }
      return rejectWithValue(error);
    }
  }
);

export const getProfile = createAsyncThunk<ProfileResponse, any, { rejectValue: AxiosError }>(
  'main/getProfile',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      // Set the authentication token for API calls
      setAuthToken(authService.getToken());

      let endpoint;
      const userRole = authService.getUserRole();

      // Determine the endpoint based on user role
      if (userRole === 'superAdmin') {
        endpoint = 'api/users/profile';
      } else if (userRole === 'healthPerson') {
        endpoint = 'api/sites/profile';
      } else if (userRole === 'doctor') {
        endpoint = 'api/doctors/profile';
      } else {
        throw new Error('Invalid user role');
      }

      // Make the API call
      const myProfile = await axios.get<ProfileResponse>(keys.api_key + endpoint);

      localStorage.setItem('/:akdfk23;//', myProfile?.data.data.doctor_id);
      localStorage.setItem('sitePersonName', myProfile?.data?.data?.name);

      return myProfile.data;
    } catch (error: any) {
      // Call the error handler
      apiErrorHandler(error, dispatch, LogOut);

      return rejectWithValue(error);
    }
  }
);
